import { AddressType, StateAcronym } from '@/types/CustomerInfo'
import { cityValidator, phoneValidator, zipValidator } from '@csc/dls'
import { useEffect } from 'react'
import useValidatedField from './useValidatedField'
import { mapStateToAcronym, mapStateToFullName } from '@/helpers'

const truthy = value => !!value

type ValidatedFormSetter<T> = (plainObject: T) => void

type ValidatedAddressFormInput = {
  address?: AddressType
  showFieldErrors: boolean
}

const validateZip = zip => {
  if (!zip) {
    return 'Required'
  }
  if (!`${zip}`.match(zipValidator.pattern.value)) {
    return zipValidator.pattern.message
  }
  return ''
}

const validatePhone = phone => {
  if (!phone) {
    return 'Required'
  }
  const validatedValue = phoneValidator.validate.validPhoneNumber(phone)
  if (validatedValue && typeof validatedValue === 'string') {
    return validatedValue
  }
  return ''
}

const validateCity = city => {
  if (!city) {
    return 'Required'
  }
  if (!`${city}`.match(cityValidator.pattern.value)) {
    return 'Invalid City'
  }
  return ''
}

const validateState = function (state) {
  if (!state) {
    return 'Required'
  } else if (mapStateToAcronym(state) === mapStateToFullName(state)) {
    return 'Invalid state'
  }
  return ''
}

const EmptyAddress = {
  firstName: '',
  lastName: '',
  company: '',
  street1: '',
  street2: '',
  stateAcronym: '' as '',
  state: '' as '',
  city: '',
  zip: '',
  phone: '',
}

const required = value => !!`${!!value ? value : ''}`.trim() ? '' : 'Required'

const useValidatedAddressForm = function ({
  address = EmptyAddress,
  showFieldErrors,
}: ValidatedAddressFormInput) {
  const shouldShowFieldErrors = () => showFieldErrors
  const firstName = useValidatedField<string>(address?.firstName || '', required, shouldShowFieldErrors);
  const lastName = useValidatedField<string>(address?.lastName || '', required, shouldShowFieldErrors);
  const company = useValidatedField<string>(address?.company || '');
  const street1 = useValidatedField<string>(address?.street1 || '', required, shouldShowFieldErrors);
  const street2 = useValidatedField<string>(address?.street2 || '');
  const city = useValidatedField<string>(address?.city || '', validateCity, shouldShowFieldErrors);
  const stateAcronym = useValidatedField<string>(address.stateAcronym, validateState, shouldShowFieldErrors)
  const zip = useValidatedField<string>(address?.zip || '', validateZip, shouldShowFieldErrors);
  const phone = useValidatedField<string>(address?.phone || '', validatePhone, shouldShowFieldErrors);

  const fields = {
    firstName,
    lastName,
    company,
    street1,
    street2,
    city,
    stateAcronym,
    zip,
    phone,
  }

  const setValue: ValidatedFormSetter<AddressType> = (plainObject) => (
    Object.keys(fields).forEach((key: keyof (typeof fields)) => {
      const field = fields[key]
      const value = plainObject[key] || ''
      field.setValue(value)
    })
  )

  useEffect(() => {
    setValue(address)
  }, [address])

  const value: AddressType = {
    ...address,
    firstName: firstName.value,
    lastName: lastName.value,
    company: company.value,
    street1: street1.value,
    street2: street2.value,
    city: city.value,
    stateAcronym: stateAcronym.value as StateAcronym,
    zip: zip.value,
    phone: phone.value,
    state: mapStateToFullName(stateAcronym.value) || '',
  }

  const values = Object.values(fields)
  const issues = values.map(field => field.issues).filter(truthy)
  const showIssues = shouldShowFieldErrors() && values.map(field => field.showIssues).some(truthy)
  const refsWithIssues = values.map(field => {
    if (field.issues) {
      return field.ref
    }
  }).filter(ref => ref)

  const clearFrom = () => setValue(EmptyAddress)

  return {
    value,
    setValue,
    issues,
    showIssues,
    refsWithIssues,
    fields,
    clearFrom,
  }
}

export default useValidatedAddressForm
